import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from '../../lib/utils'

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-home-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-home-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-home-primary text-home-primary-foreground hover:bg-home-primary/90 shadow-lg-custom",
        destructive: "bg-home-destructive text-home-destructive-foreground hover:bg-home-destructive/90",
        outline: "border border-home-input bg-home-background hover:bg-home-accent hover:text-home-accent-foreground",
        secondary: "bg-home-secondary text-home-secondary-foreground hover:bg-home-secondary/80",
        ghost: "hover:bg-home-accent hover:text-home-accent-foreground",
        link: "text-home-primary underline-offset-4 hover:underline",
        hero: "bg-home-accent text-home-accent-foreground hover:bg-home-accent/90 shadow-home-accent font-semibold",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
